import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAtomValue } from 'jotai';
import { Box, Divider, Paper, Typography } from '@mui/material';
import CustomerListWrapper, { toRecipient } from './CustomerListWrapper';
import ContentBodyBox from '../../../Components/ContentBodyBox';
import MassiveFiltersStack from './MassiveFiltersStack';
import DetailsHeader from './DetailsHeader';
import { userBranchesAtom } from '../../../Atoms';
import { createNewDate, compareDates } from '../../../Helpers/dates';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    minHeight: 0,
  },
  body: {
    flexGrow: 1,
    display: 'grid',
    gridTemplateColumns: 'minmax(320px, 2fr) 3fr',
    gridTemplateRows: 'minmax(0, 1fr)', //columns scroll inside the dialog height
    gap: '16px',
    width: '100%',
    minHeight: 0,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minHeight: 0,
  },
  messagePaper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    p: '12px 16px',
  },
  filtersPaper: {
    flexShrink: 0,
    maxHeight: '40%',
    overflow: 'auto',
    p: '12px 16px',
  },
};

const DetailsContent = function DetailsContent({
  setSearchValues,
  removeCustomersFromBatch,
  searchValues = null,
  ObserverWrapper = null,
  filters = null,
  scheduledOnDate = null,
  waveSections = [],
  recipientsCount = 0,
  firstRecipient = null,
  campaign = null,
  massive = null,
  templateName = '',
}) {
  const intl = useIntl();
  const branches = useAtomValue(userBranchesAtom);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [customersToRemove, setCustomersToRemove] = useState([]);

  //show a message right away instead of an empty box
  useEffect(() => {
    if (!selectedRecipient && firstRecipient)
      setSelectedRecipient(toRecipient(firstRecipient));
  }, [firstRecipient]);

  const handleChangeCustomersToRemove = (e) => {
    e.stopPropagation();
    const { checked, value } = e.target;

    if (value) {
      setCustomersToRemove((oldArray) =>
        checked
          ? [...oldArray, value]
          : oldArray.filter((item) => item !== value)
      );
    }
  };

  const handleCleanCustomers = () => {
    setCustomersToRemove([]);
  };

  const handleRemoveFromBatch = () => {
    const { IsSMS } = filters;
    const payloadObject = {
      isSMS: IsSMS,
      smsIds: IsSMS ? customersToRemove : [],
      emailsIds: IsSMS ? [] : customersToRemove,
    };
    removeCustomersFromBatch(
      payloadObject,
      customersToRemove,
      handleCleanCustomers
    );
  };

  return (
    <Box sx={styles.root}>
      <DetailsHeader
        campaign={campaign}
        massive={massive}
        filters={filters}
        templateName={templateName}
      />
      <Box sx={styles.body}>
        <CustomerListWrapper
          recipientsCount={recipientsCount}
          messageAlreadySent={
            Boolean(scheduledOnDate) &&
            compareDates(createNewDate(scheduledOnDate), new Date(), 'smaller')
          }
          searchValues={searchValues}
          setSearchValues={setSearchValues}
          ObserverWrapper={ObserverWrapper}
          handleSelectCustomer={setSelectedRecipient}
          selectedCustomer={selectedRecipient?.id}
          handleChangeCustomersToRemove={handleChangeCustomersToRemove}
          customersToRemove={customersToRemove}
          cleanCustomersToRemove={handleCleanCustomers}
          handleRemoveFromBatch={handleRemoveFromBatch}
        />
        <Box sx={styles.rightColumn}>
          <Paper variant="outlined" sx={styles.messagePaper}>
            <Box sx={styles.sectionHeader}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {intl.messages.MESSAGE}
              </Typography>
              {selectedRecipient && (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                  noWrap
                >
                  {`${selectedRecipient.fullName} · ${selectedRecipient.contact ?? ''}`}
                </Typography>
              )}
            </Box>
            <Divider />
            {selectedRecipient ? (
              <ContentBodyBox
                content={selectedRecipient.message}
                styles={{
                  root: { border: 'none', borderRadius: 0, minHeight: 0 },
                  //sms are plain text: keep their line breaks
                  content: {
                    fontFamily: 'Roboto',
                    whiteSpace: filters?.IsSMS ? 'pre-wrap' : 'normal',
                  },
                }}
              />
            ) : (
              <Box sx={{ display: 'flex', flexGrow: 1, placeItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', width: '100%' }}
                  align="center"
                >
                  {intl.messages.SELECT_RECIPIENT}
                </Typography>
              </Box>
            )}
          </Paper>
          <Paper variant="outlined" sx={styles.filtersPaper}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', mb: '8px' }}
            >
              {intl.messages.APPLIED_FILTERS}
            </Typography>
            <MassiveFiltersStack
              filters={filters}
              waveSections={waveSections}
              branches={branches}
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default DetailsContent;
